/**
 * Alta y cambio de un tratamiento de colaboradores desde el Catálogo de
 * precios (administración, 27/10/2026).
 *
 * Edita el TARIFARIO DE COLABORADORES (el mismo que Colaboradores →
 * Tarifario) por sus rutas, `/api/colaboradores/catalogos/tratamientos`:
 * una sola fuente, lo que se cambia aquí se ve allí y al revés. Con la misma
 * pinta que el diálogo del resto del catálogo (ElementoPrecioDialog), pero
 * con sus campos: nombre, PVP, si va con o sin IVA (exento: no se divide
 * entre 1,21 al calcular el reparto del médico) y una nota libre.
 *
 * El importe se escribe como se dice («1.234,50 €», «12,50»); se lee con la
 * misma regla que el tarifario de Colaboradores (`leerImporte`) y, si no se
 * entiende, se dice y la ventana sigue abierta. El servidor vuelve a mirar
 * quién es (403), rechaza un nombre repetido (409) y lo apunta en Control
 * rastreo. Aquí no hay ni un precio ni un nombre de la clínica.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { importeParaCampo, leerImporte } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

export const RUTA_TRATAMIENTOS_COLAB = '/api/colaboradores/catalogos/tratamientos';

const VACIO = { nombre: '', precio: '', sin_iva: 'con', nota: '' };

/**
 * @param destino  null (cerrado), {} (alta) o {elemento} (edición).
 * @param onGuardado  se llama tras guardar; `onClose`, al cerrar.
 */
export default function TratamientoColaboradorDialog({ destino, onClose, onGuardado }) {
  const elemento = destino?.elemento || null;
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!destino) return;
    const e = destino.elemento;
    setForm(e ? {
      nombre: e.nombre || '', precio: importeParaCampo(e.pvp),
      sin_iva: e.sin_iva ? 'sin' : 'con', nota: e.nota || '',
    } : VACIO);
  }, [destino]);

  if (!destino) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const enviar = async (e) => {
    e.preventDefault();
    const nombre = form.nombre.trim();
    if (!nombre) { toast.error('Escribe el nombre.'); return; }
    const precio = leerImporte(form.precio);
    if (precio === null || Number.isNaN(precio)) {
      toast.error('Escribe el PVP en euros, por ejemplo 1.234,50.');
      return;
    }
    const cuerpo = { nombre, precio, sin_iva: form.sin_iva === 'sin', nota: form.nota.trim() };
    setGuardando(true);
    try {
      if (elemento) await api.put(`${RUTA_TRATAMIENTOS_COLAB}/${elemento.id}`, cuerpo);
      else await api.post(RUTA_TRATAMIENTOS_COLAB, cuerpo);
      toast.success(elemento ? 'Guardado' : 'Tratamiento añadido');
      onGuardado && onGuardado();
      onClose && onClose();
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido guardar'));
      // Lo retiró otra persona con la ventana abierta: la lista se recarga.
      if (elemento && err?.response?.status === 404) onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && onClose) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{elemento ? 'Editar tratamiento de colaboradores' : 'Nuevo tratamiento de colaboradores'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enviar} className="space-y-3" data-testid="colab-tratamiento-formulario">
          <p className="text-xs text-muted-foreground">
            En: el tarifario de colaboradores (el mismo que Colaboradores → Tarifario). Un cambio vale para los
            actos que se registren desde ahora; lo ya registrado conserva su importe.
          </p>
          <div>
            <Label htmlFor="colab-tratamiento-nombre">Nombre</Label>
            <Input id="colab-tratamiento-nombre" value={form.nombre} onChange={(e) => set('nombre', e.target.value)}
              required maxLength={120} autoComplete="off" data-testid="colab-tratamiento-campo-nombre" />
          </div>
          <div>
            <Label htmlFor="colab-tratamiento-precio">PVP</Label>
            <Input id="colab-tratamiento-precio" value={form.precio} onChange={(e) => set('precio', e.target.value)}
              placeholder="1.234,50 €" inputMode="decimal" autoComplete="off"
              data-testid="colab-tratamiento-campo-precio" />
          </div>
          <div>
            <Label htmlFor="colab-tratamiento-iva">IVA</Label>
            <select id="colab-tratamiento-iva" value={form.sin_iva} onChange={(e) => set('sin_iva', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="colab-tratamiento-campo-iva">
              <option value="con">Con IVA (el PVP lo lleva: se divide entre 1,21 al calcular el reparto)</option>
              <option value="sin">Sin IVA (exento: el PVP no se divide)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="colab-tratamiento-nota">Nota</Label>
            <Textarea id="colab-tratamiento-nota" rows={3} value={form.nota} onChange={(e) => set('nota', e.target.value)}
              maxLength={2000} data-testid="colab-tratamiento-campo-nota" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="colab-tratamiento-guardar">
              {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {elemento ? 'Guardar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
